/**
 * Host-picker languages for the Boer TenantApp marketing landing
 * (theboer.co.za PRE-auth page — uploaded HTML, not the signed-in MFE).
 * Same five codes as legacy-client UserProfile / Navbar and standalone LANGUAGE_OPTIONS:
 * en / af / es / it / ja.
 *
 * Keys match `data-i18n` on `standalone/landing-pages/boer.html`.
 * Brand names and register codes stay as-is.
 */
export const BOER_LANDING_LOCALES = ["en", "af", "es", "it", "ja"] as const;
export type BoerLandingLocale = (typeof BOER_LANDING_LOCALES)[number];

export const BOER_LANDING_COPY: Record<BoerLandingLocale, Record<string, string>> = {
  en: {
    "doc.title": "Boer by Saltbird",
    "nav.homeAria": "Boer home",
    "nav.chain": "How it connects",
    "nav.product": "Product",
    "nav.compare": "Compare",
    "nav.packhouses": "For packhouses",
    "nav.pricing": "Pricing",
    "cta.signIn": "Sign in",
    "cta.startFree": "Start free",
    "cta.startPro": "Start Pro",
    "cta.seeHow": "See how it connects ↓",
    "cta.talkPackhouse": "Talk to us about your packhouse",
    "cta.talkToUs": "Talk to us",
    "lang.aria": "Language",
    "hero.eyebrow": "For South African fruit growers",
    "hero.title": "The farm app that goes through the packhouse gate.",
    "hero.titleHtml": 'The farm app that goes <span class="thread">through the packhouse gate.</span>',
    "hero.lead":
      "Spray diary, harvest bins and export compliance on one record — from the orchard to the weighbridge and back to the block it came from. Built in the Western Cape, priced in Rand, free for growers.",
    "hero.note": "No card. No hectare limit. Works on the phone your foreman already carries.",
    "hero.credit": "Franschhoek, Western Cape — footage © Paul Knipe",
    "stats.eyebrow": "From the block to the bill of lading",
    "stats.title": "One record. Six hand-offs. Nothing typed twice.",
    "stats.lead":
      "A field in a diary app is whatever someone typed. A block in Boer is the orchard the registry knows, the bin the gate scanned, and the kilograms the weighbridge saw.",
    "stats.taps": "taps to log a spray at the row end",
    "stats.handoffs": "hand-offs on the same record",
    "stats.retype": "re-typing at the packhouse gate",
    "stats.free": "for growers, for good",
    "record.aria": "Example bin record",
    "record.weighed": "Weighed",
    "record.sprayed": "Sprayed",
    "record.sprayedSub": "Cyanamid · 14 days PHI",
    "record.sprayedVal": "12 Jun · safe from 26 Jun",
    "record.picked": "Picked",
    "record.pickedSub": "Team Willem · 6 pickers",
    "record.sent": "Sent",
    "record.sentSub": "Dispatch note DN-2287 · 24 bins",
    "record.adopted": "Adopted at gate",
    "record.adoptedSub": "RFID read · no re-typing",
    "record.weighedWhat": "Weighed",
    "record.weighedSub": "Packhouse weighbridge",
    "record.return": "Return",
    "record.returnSub": "Posts back to this block",
    "record.afterPackout": "after packout",
    "record.blockWeek": "Block ZA-OB-1142-07 this week",
    "trust.label": "Shaped for the registers South African growers already answer to.",
    "loop.eyebrow": "Why growers switch",
    "loop.title": "Everything else is a diary. Boer is the whole chain.",
    "loop.lead":
      "A diary app stops at a printed label. Boer names the bin at the pick, hands it through the gate, and brings the packhouse's weight back to the block it came from.",
    "loop.blockCap": "The real orchard",
    "loop.blockSpan": "Registered PUC and orchard refs, cultivar codes, approved markets — not a shape someone drew.",
    "loop.pickCap": "Logged at the row end",
    "loop.pickSpan": "Spray cleared by PHI, bin named, team named, time stamped.",
    "loop.gateCap": "The bin goes through",
    "loop.gateSpan": "Dispatch note, RFID adoption at the gate, weighed and onto the line — no re-typing.",
    "loop.returnCap": "Back to the block",
    "loop.returnSpan": "Packed, palletised, shipped — tonnes and Rands post back to the orchard and the team that picked it.",
    "loop.oneRecord": "ONE RECORD",
    "record.binId": "Bin 0412",
    "record.ref": "ZA-OB-1142-07 · Nadorcott · 2026 season",
    "record.footTotal": "9.84 t · 24 bins",
    "trust.puc": "PUC · orchard",
    "trust.fcm": "FCM register",
    "trust.lnum": "L-numbers",
    "loop.blockN": "Block",
    "loop.pickN": "Pick",
    "loop.gateN": "Gate",
    "loop.returnN": "Return",
    "nav.menuAria": "Menu",
    "loop.farmGateFarm": "Farm → gate → farm",
    "chain.block": "Block",
    "chain.blockP": "Your registered orchard, synced from the registry, with cultivars and markets on the card.",
    "chain.spray": "Spray",
    "chain.sprayP": "Product, rate, water, operator, weather. The PHI sets the safe-harvest date itself.",
    "chain.pick": "Pick",
    "chain.pickP": "Every fill names a bin — block, cultivar, picker, time.",
    "chain.gate": "Gate",
    "chain.gateP": "The packhouse gate reads the bin and adopts the fill. No paper, no missing bins.",
    "chain.pack": "Pack",
    "chain.packP": "Weighed, graded, packed on the record you started.",
    "chain.return": "Return",
    "chain.returnP": "Tonnes and Rands come back to the block. Economics on real returns.",
    "product.eyebrow": "The product",
    "product.title": "Made for the person at the row end, not the one at the desk.",
    "product.lead":
      "The foreman logs the spray at six in the morning, not the office at five in the afternoon. Boer is built for that — and the office gets a register that writes itself.",
    "capture.eyebrow": "Capture",
    "capture.title": "Three taps from the home screen. Readable in the sun.",
    "capture.p":
      "Home, Farms, Harvest and one big +. Light screens, big targets, the block picked from where you're standing. Works offline in the orchard and syncs at the shed.",
    "capture.offline": "offline · season '26",
    "capture.li1": "Spray with the product's L-number, rate, water and tank mix",
    "capture.li2": "Fertilise, fertigate, irrigate, scout, trap round, sanitation",
    "capture.li3": "Harvest fill with the bin named, the team named, the time stamped",
    "phone.aria": "Phone showing a spray job being captured",
    "phone.sprayJob": "Spray job",
    "phone.kProduct": "Product",
    "phone.kRate": "Rate · water",
    "phone.kEquip": "Equipment · operator",
    "phone.kWeather": "Weather at 06:40",
    "phone.kSafe": "Safe harvest from",
    "phone.navHome": "Home",
    "phone.navFarms": "Farms",
    "phone.navHarvest": "Harvest",
    "phone.navMore": "More",
    "registry.eyebrow": "Registry truth",
    "registry.title": "Your blocks are the real blocks.",
    "registry.p":
      "Boer syncs your production units and orchards from the registry — PUC, orchard refs, cultivar codes, market registrations and certificate expiry. Change it once; the inspector and the packhouse see the same thing.",
    "registry.li1": "One block row per registered orchard, for life",
    "registry.li2": "Approved markets and GGN expiry on the block card",
    "registry.li3": "Import your KML or draw it on the map — the ref stays the same",
    "registry.synced": "Registry-synced",
    "registry.cardSub": "ZA-PU-1142 · 4 orchards · GGN expires in 84 days",
    "registry.thOrchard": "Orchard",
    "registry.thCultivar": "Cultivar",
    "registry.thMarkets": "Markets",
    "registry.thSafe": "Safe harvest",
    "registry.phiNotClear": "PHI not clear",
    "registry.clear": "clear",
    "bins.eyebrow": "The bin loop",
    "bins.title": "Know what left the farm and what the packhouse received. To the kilogram.",
    "bins.p":
      "Empty, full, in store, sent, adopted at the gate, weighed. Custody moves with the bin; the weight comes back to you. When the packout differs from what you sent, you know which bin, which block, which morning.",
    "bins.li1": "Dispatch notes built from the bins you actually filled",
    "bins.li2": "RFID or QR — the gate adopts the fill without re-typing",
    "bins.li3": "Tonnage ladder per block, per cultivar, per team",
    "bins.thBin": "Bin",
    "bins.thBlock": "Block",
    "bins.thTeam": "Team",
    "bins.thGate": "Gate",
    "bins.thSent": "Sent kg",
    "bins.thWeighed": "Weighed kg",
    "bins.more": "… 20 more bins",
    "bins.adopted": "24 / 24 adopted",
    "comp.eyebrow": "Compliance",
    "comp.title": "The register writes itself.",
    "comp.p":
      "Trap rounds, orchard sanitation, safe-harvest dates and certificate expiry live on the same record as your sprays. When the auditor calls, the Locker shows what's ready and what isn't — and exports the register in the shape they ask for.",
    "comp.exports": "exports · season '26",
    "comp.li1": "FCM trap rounds and sanitation, anchored to your season dates",
    "comp.li2": "Safe-harvest board: which blocks may be picked today",
    "comp.li3": "GlobalG.A.P.-shaped spray register, harvest register, inspection pack",
    "comp.auditIn": "Klipfontein · audit in 19 days",
    "comp.toFix": "2 to fix",
    "comp.readyFor": "ready for the inspector",
    "comp.sprayReg": "Spray register",
    "comp.sprayRegSub": "212 applications · every one with an L-number",
    "comp.complete": "complete",
    "comp.fcm": "FCM trap rounds",
    "comp.fcmSub": "Weekly since 1 Oct · 14 traps",
    "comp.san": "Orchard sanitation",
    "comp.sanSub": "Fallen-fruit register",
    "comp.lastDays": "last: 2 days ago",
    "comp.safeBoard": "Safe-harvest board",
    "comp.safeBoardSub": "Block 03 · PHI clears 9 Jul",
    "comp.oneBlock": "1 block not clear",
    "comp.vault": "Document vault",
    "comp.vaultSub": "SIZA, water-use licence, FBO",
    "comp.docs": "11 documents",
    "comp.ggap": "GlobalG.A.P. certificate",
    "comp.expires": "expires in 84 days",
    "compare.eyebrow": "Compare",
    "compare.title": "Good diaries. One chain.",
    "compare.lead":
      "Farmable is a fine spray diary and Tend is a fine crop planner. Neither knows your PUC, neither follows the bin through the gate, and neither brings the weight back.",
    "compare.thBoer": "Boer",
    "compare.thBoerSub": "Saltbird · South Africa",
    "compare.thFarmable": "Farmable",
    "compare.thFarmableSub": "Oslo, Norway",
    "compare.thTend": "Tend",
    "compare.thTendSub": "California, USA",
    "compare.r1": "Blocks are your registered orchards",
    "compare.r1s": "PUC · orchard refs · cultivar codes · markets",
    "compare.r1b": "Registry-synced",
    "compare.r1f": "Self-declared fields",
    "compare.r1t": "Self-declared beds",
    "compare.r2": "Bin custody from pick to packhouse gate",
    "compare.r2b": "Named bin, dispatch, gate adoption",
    "compare.r2f": "Printed harvest label",
    "compare.r2t": "Lot ID and QR",
    "compare.r3": "Packhouse weights come back to the block",
    "compare.r4": "FCM trap rounds and sanitation register",
    "compare.r4s": "DALRRD-shaped, anchored to your season",
    "compare.r4f": "Generic monitoring job",
    "compare.r5": "Safe-harvest date from the PHI, per block",
    "compare.r5f": "Label alerts (full data UK only)",
    "compare.r6": "Certificate expiry and audit-readiness check",
    "compare.r6b": "Compliance Locker",
    "compare.r6f": "Compliance dashboard",
    "compare.r6t": "Reports",
    "compare.r7": "South African product register",
    "compare.r7s": "Act 36 L-numbers on every spray",
    "compare.r7f": "9 registers, none SA",
    "compare.r8": "Spray diary on the phone, offline",
    "compare.r8t": "companion app",
    "compare.r9": "GlobalG.A.P.-shaped register export",
    "compare.r9t": "Partial",
    "compare.r10": "Works with your packhouse's system",
    "compare.r10b": "Same record",
    "compare.r10f": "Enterprise portal, separate",
    "compare.r11": "Price for a grower",
    "compare.r11s": "per farm, per year",
    "compare.r11b": "Free · Pro R4 950",
    "compare.r11f": "Free (5 jobs) · Pro R6 499",
    "compare.r11t": "Free · Pro ≈ R4 300 (USD)",
    "compare.note":
      "Competitor details from their public websites, help centres and app-store listings, August 2026. Farmable and Tend are trademarks of their owners. Items marked season '26 ship before the 2026 stone-fruit season.",
    "onramp.eyebrow": "Already on Farmable?",
    "onramp.title": "Bring it across in an afternoon.",
    "onramp.p":
      "Connect your Farmable account and your fields, products, spray history, teams and timesheets come in as Boer blocks and records. Keep both running until you're sure.",
    "onramp.s1": "Paste your Farmable API key",
    "onramp.s2": "Match your fields to your registered orchards — Boer suggests the pairs",
    "onramp.s3": "Three seasons of history land on the right blocks",
    "ph.eyebrow": "For packhouses and co-ops",
    "ph.title": "Bring your whole supplier base.",
    "ph.p":
      "Every grower who supplies you gets Boer free. Their sprays, safe-harvest dates and bins arrive on your record before the truck does. One register for a hundred farms — and the returns you post go straight back to their blocks.",
    "ph.s1": "Your supplier register becomes their farm list",
    "ph.s2": "Your gate adopts their bins; your weighbridge closes the loop",
    "ph.s3": "Your packout and returns post back to the grower's block",
    "pricing.eyebrow": "Pricing",
    "pricing.title": "Free for growers. Rand, per farm, nothing per hectare.",
    "pricing.lead": "A farm is a farm — one PUC or six, ten workers or two hundred. The price doesn't move when the orchard grows.",
    "plan.grower": "Grower",
    "plan.forever": "forever",
    "plan.growerWho":
      "Every grower. Free for good if you supply a packhouse on Boer — and still free if you don't.",
    "plan.g1": "Unlimited blocks, users and notes",
    "plan.g2": "Spray, nutrition, irrigation and scouting records",
    "plan.g3": "FCM trap rounds, sanitation, safe-harvest board",
    "plan.g4": "Harvest bins, dispatch, gate adoption, weights back",
    "plan.g5": "Registry sync, certificate expiry, document vault",
    "plan.pro": "Pro",
    "plan.perFarm": "per farm · per year",
    "plan.proWho": "For the farm that gets audited. About R413 a month, VAT included.",
    "plan.p1": "Everything in Grower",
    "plan.p2": "Audit exports: spray register, FCM report, harvest register, inspection pack",
    "plan.p3": "Product inventory with cost per block",
    "plan.p4": "Clock-in, timesheets and piece rate",
    "plan.p5": "Advisor access and block economics on real returns",
    "plan.p6": "API access",
    "plan.mostFarms": "Most farms",
    "plan.pack": "Packhouse & co-op",
    "plan.letsTalk": "Let's talk",
    "plan.packWho": "Your supplier base on one register, your gate and weighbridge closing the loop.",
    "plan.pk1": "Every supplying grower on Grower, free",
    "plan.pk2": "Supplier compliance view before the truck arrives",
    "plan.pk3": "Gate and weighbridge integration",
    "plan.pk4": "Returns posted back to growers' blocks",
    "plan.pk5": "Onboarding on site, in Afrikaans or English",
    "pricing.note":
      "Prices in South African Rand, VAT included. Pro is billed per farm per year — not per hectare, not per user. Cancel any time; your data exports with you.",
    "faq.eyebrow": "Questions growers ask",
    "faq.title": "Straight answers.",
    "faq.q1": "Does it work with no signal in the orchard?",
    "faq.a1":
      "Harvest fills already queue offline and sync at the shed. Every other capture — sprays, trap rounds, scouting — goes offline before the 2026 stone-fruit season.",
    "faq.q2": "Do I need my packhouse to be on Boer?",
    "faq.a2":
      "No. Boer is a complete farm record on its own. When your packhouse joins, the gate and weighbridge close the loop automatically — nothing changes on your side.",
    "faq.q3": "Who owns my data?",
    "faq.a3":
      "You do. It's stored in South Africa, handled under POPIA, and you can export everything at any time. Your packhouse sees only what you share with it.",
    "faq.q4": "I'm on Farmable. Do I lose my history?",
    "faq.a4":
      "No. Connect your Farmable account and three seasons of fields, sprays, teams and timesheets come across onto your registered orchards. Keep both running until you're sure.",
    "faq.q5": "Which crops?",
    "faq.a5":
      "Citrus, stone fruit, pome fruit, table grapes, avocados and macadamias first — the export crops with FCM, PHI and GlobalG.A.P. pressure. Other crops work; the statutory registers are tuned for these.",
    "faq.q6": "Which languages are available?",
    "faq.a6":
      "English, Afrikaans, Español, Italiano and Japanese. Use the language control at the top of this page. The signed-in app follows the same choice — language is set once on this host.",
    "final.title": "Start with one block this week.",
    "final.p":
      "Add your PUC, log Monday's spray, fill a bin on the first pick. By the end of the season the register is written and the packhouse has already seen it.",
    "foot.product": "Product",
    "foot.compare": "Compare",
    "foot.pricing": "Pricing",
    "foot.packhouses": "Packhouses",
    "foot.whatsNew": "What's new",
    "foot.privacy": "Privacy",
    "foot.terms": "Terms",
    "foot.contact": "Contact",
    "foot.saltbird": "Boer is a Saltbird company · Made in the Western Cape",
    "foot.legal":
      "© 2026 Saltbird. Boer is independent and not affiliated with AgriOps, DALRRD, GlobalG.A.P., SIZA or PPECB. Photography: Freshness First, used with permission. Franschhoek footage © Paul Knipe.",
  },
  af: {
    "doc.title": "Boer deur Saltbird",
    "nav.homeAria": "Boer tuis",
    "nav.chain": "Hoe dit inskakel",
    "nav.product": "Produk",
    "nav.compare": "Vergelyk",
    "nav.packhouses": "Vir pakhuise",
    "nav.pricing": "Pryse",
    "cta.signIn": "Teken in",
    "cta.startFree": "Begin gratis",
    "cta.startPro": "Begin Pro",
    "cta.seeHow": "Sien hoe dit inskakel ↓",
    "cta.talkPackhouse": "Praat met ons oor jou pakhuis",
    "cta.talkToUs": "Praat met ons",
    "lang.aria": "Taal",
    "hero.eyebrow": "Vir Suid-Afrikaanse vrugteboere",
    "hero.title": "Die plaas-app wat deur die pakhuishek gaan.",
    "hero.titleHtml": 'Die plaas-app wat <span class="thread">deur die pakhuishek gaan.</span>',
    "hero.lead":
      "Spuitdagboek, oeskratte en uitvoernakoming op een rekord — van die boord tot by die weegbrug en terug na die blok waaruit dit gekom het. Gebou in die Wes-Kaap, geprys in Rand, gratis vir boere.",
    "hero.note": "Geen kaart. Geen hektaarlimiet. Werk op die foon wat jou voorman alreeds dra.",
    "hero.credit": "Franschhoek, Wes-Kaap — beeldmateriaal © Paul Knipe",
    "stats.eyebrow": "Van die blok tot die skeepsvragbrief",
    "stats.title": "Een rekord. Ses oordragte. Geen duplisering.",
    "stats.lead":
      "’n Veld in ’n dagboek-app is wat iemand tik. ’n Blok in Boer is die boord op die register, die krat wat by die hek ingelees is, en die kilogramme wat die weegbrug gemeet het.",
    "stats.taps": "stappe om ’n spuit by die ryeinde aan te teken",
    "stats.handoffs": "oordragte op dieselfde rekord",
    "stats.retype": "dubbele inskrywing by die pakhuishek",
    "stats.free": "vir boere, vir altyd",
    "record.aria": "Voorbeeld-kratrekord",
    "record.weighed": "Geweeg",
    "record.sprayed": "Gespuit",
    "record.sprayedSub": "Cyanamid · 14 dae PHI",
    "record.sprayedVal": "12 Jun · veilig vanaf 26 Jun",
    "record.picked": "Gepluk",
    "record.pickedSub": "Span Willem · 6 plukkers",
    "record.sent": "Gestuur",
    "record.sentSub": "Versendingsnota DN-2287 · 24 kratte",
    "record.adopted": "Aanvaar by die hek",
    "record.adoptedSub": "RFID-lesing · geen dubbele inskrywing",
    "record.weighedWhat": "Geweeg",
    "record.weighedSub": "Pakhuis-weegbrug",
    "record.return": "Terugvoer",
    "record.returnSub": "Plaas weer in hierdie blok",
    "record.afterPackout": "ná verpakking",
    "record.blockWeek": "Blok ZA-OB-1142-07 hierdie week",
    "trust.label": "Ontwerp rondom die prosesse en vereistes wat Suid-Afrikaanse produsente reeds gebruik.",
    "loop.eyebrow": "Waarom produsente oorskakel",
    "loop.title": "Alles anders is ’n dagboek. Boer is die hele ketting.",
    "loop.lead":
      "’n Dagboek-app stop by ’n gedrukte etiket. Boer benoem die krat tydens pluk, stuur dit deur die hek en bring die pakhuis se gewig terug na die blok waar dit vandaan kom.",
    "loop.blockCap": "Die regte boord",
    "loop.blockSpan": "Geregistreerde PUC- en boordverwysings, kultivarkodes, goedgekeurde markte — nie ’n vorm wat iemand getrek het nie.",
    "loop.pickCap": "Aangeteken by die ryeinde",
    "loop.pickSpan": "Spuit vrygeskakel deur PHI, krat genoem, span genoem, tyd gestempel.",
    "loop.gateCap": "Die krat gaan deur",
    "loop.gateSpan": "Versendingsnota, RFID-aanname by die hek, geweeg en op die lyn — geen dubbele inskrywing.",
    "loop.returnCap": "Terug na die blok",
    "loop.returnSpan": "Verpak, op palette, verskeep — tonnemaat en randwaarde word teruggekoppel na die boord en die span wat dit gepluk het.",
    "loop.oneRecord": "EEN REKORD",
    "record.binId": "Krat 0412",
    "record.ref": "ZA-OB-1142-07 · Nadorcott · 2026-seisoen",
    "record.footTotal": "9.84 t · 24 kratte",
    "trust.puc": "PUK · boord",
    "trust.fcm": "FCM registrasie",
    "trust.lnum": "L-nommers",
    "loop.blockN": "Blok",
    "loop.pickN": "Pluk",
    "loop.gateN": "Hek",
    "loop.returnN": "Terugkeer",
    "nav.menuAria": "Spyskaart",
    "loop.farmGateFarm": "Plaas → hek → plaas",
    "chain.block": "Blok",
    "chain.blockP": "Jou geregistreerde boord, gesinkroniseer uit die register, met kultivars en markte op die kaart.",
    "chain.spray": "Spuit",
    "chain.sprayP": "Produk, dosis, water, operateur, weer. Die PHI stel self die veilige-oesdatum.",
    "chain.pick": "Pluk",
    "chain.pickP": "Elke vulling noem ’n krat — blok, kultivar, plukker, tyd.",
    "chain.gate": "Hek",
    "chain.gateP": "Die pakhuishek lees die krat en neem die vulling oor. Geen papier, geen vermiste kratte.",
    "chain.pack": "Pak",
    "chain.packP": "Geweeg, gegradeer, verpak op die rekord wat jy begin het.",
    "chain.return": "Terugvoer",
    "chain.returnP": "Tonne en Rande kom terug na die blok. Ekonomie op werklike opbrengste.",
    "product.eyebrow": "Die produk",
    "product.title": "Gemaak vir die persoon aan die ryeinde, nie die een by die lessenaar nie.",
    "product.lead":
      "Die voorman teken die spuit aan om sesuur die oggend, nie die kantoor om vyfuur die middag nie. Boer is daarvoor gemaak — en die kantoor kry ’n register wat homself skryf.",
    "capture.eyebrow": "Vaslegging",
    "capture.title": "Drie tikke vanaf die tuisskerm. Leesbaar in die son.",
    "capture.p":
      "Tuis, Plase, Oes en een groot +. Ligte skerms, groot teikens, die blok gekies van waar jy staan. Werk sonder internet in die boord en sinkroniseer by die skuur.",
    "capture.offline": "sonder internet · seisoen ’26",
    "capture.li1": "Spuit met die produk se L-nommer, dosis, water en tenkmengsel",
    "capture.li2": "Bemesting, fertigeer, besproeiing, verkenning, valrondte, sanitasie",
    "capture.li3": "Oesvulling met die krat genoem, die span genoem, die tyd gestempel",
    "phone.aria": "Foon wat ’n spuittaak vaslê",
    "phone.sprayJob": "Spuittaak",
    "phone.kProduct": "Produk",
    "phone.kRate": "Dosis · water",
    "phone.kEquip": "Toerusting · operateur",
    "phone.kWeather": "Weer om 06:40",
    "phone.kSafe": "Veilige oes vanaf",
    "phone.navHome": "Tuis",
    "phone.navFarms": "Plase",
    "phone.navHarvest": "Oes",
    "phone.navMore": "Meer",
    "registry.eyebrow": "Registerwaarheid",
    "registry.title": "Jou blokke is die werklike blokke.",
    "registry.p":
      "Boer sinkroniseer jou produksie-eenhede en boorde vanuit die register – PUC’s, boordverwysings, kultivarkodes, markregistrasies en sertifikaatvervaldatums. Verander dit een keer; die inspekteur en die pakhuis sien dieselfde inligting.",
    "registry.li1": "Een blokry per geregistreerde boord, vir altyd",
    "registry.li2": "Goedgekeurde markte en GGN-vervaldatum op die blokkaart",
    "registry.li3": "Voer jou KML in of teken dit op die kaart — die verwysing bly dieselfde",
    "registry.synced": "Register-gesinkroniseer",
    "registry.cardSub": "ZA-PU-1142 · 4 boorde · GGN verval oor 84 dae",
    "registry.thOrchard": "Boord",
    "registry.thCultivar": "Kultivar",
    "registry.thMarkets": "Markte",
    "registry.thSafe": "Veilige oes",
    "registry.phiNotClear": "PHI nie vry nie",
    "registry.clear": "vry",
    "bins.eyebrow": "Die krat-siklus",
    "bins.title": "Weet wat die plaas verlaat het en wat die pakhuis ontvang het. Tot op die kilogram.",
    "bins.p":
      "Leeg, vol, in stoor, gestuur, aanvaar by die hek, geweeg. Toesig beweeg saam met die krat; die gewig kom terug na jou toe. Wanneer die verpakkingsuitset verskil van wat jy gestuur het, weet jy watter krat, watter blok, watter oggend.",
    "bins.li1": "Versendingsnotas saamgestel uit die kratte wat jy werklik gevul het.",
    "bins.li2": "RFID of QR — die hek aanvaar die vulinligting sonder dubbele inskrywing",
    "bins.li3": "Tonnestang per blok, per kultivar, per span",
    "bins.thBin": "Krat",
    "bins.thBlock": "Blok",
    "bins.thTeam": "Span",
    "bins.thGate": "Hek",
    "bins.thSent": "Gestuur kg",
    "bins.thWeighed": "Geweeg kg",
    "bins.more": "… 20 kratte meer",
    "bins.adopted": "24 / 24 aangeneem",
    "comp.eyebrow": "Nakoming",
    "comp.title": "Die register skryf homself.",
    "comp.p":
      "Valrondtes, boordsanitasie, veilige-oesdatums en sertifikaatverval leef op dieselfde rekord as jou spuite. Wanneer die ouditeur bel, wys die Locker wat gereed is en wat nie — en voer die register uit in die vorm wat hulle vra.",
    "comp.exports": "uitvoere · seisoen ’26",
    "comp.li1": "FCM-valrondtes en sanitasie, vasgemaak aan jou seisoendatums",
    "comp.li2": "Veilige-oesbord: watter blokke mag vandag gepluk word",
    "comp.li3": "GlobalG.A.P.-vormige spuitregister, oesregister, inspeksiepak",
    "comp.auditIn": "Klipfontein · oudit oor 19 dae",
    "comp.toFix": "2 om reg te maak",
    "comp.readyFor": "gereed vir die inspekteur",
    "comp.sprayReg": "Spuitregister",
    "comp.sprayRegSub": "212 toepassings · elkeen met ’n L-nommer",
    "comp.complete": "voltooi",
    "comp.fcm": "FCM-valrondtes",
    "comp.fcmSub": "Weekliks sedert 1 Okt · 14 valle",
    "comp.san": "Boordsanitasie",
    "comp.sanSub": "Register van gevalle vrugte",
    "comp.lastDays": "laaste: 2 dae gelede",
    "comp.safeBoard": "Veilige-oesbord",
    "comp.safeBoardSub": "Blok 03 · PHI vry op 9 Jul",
    "comp.oneBlock": "1 blok nie vry nie",
    "comp.vault": "Dokumentkluis",
    "comp.vaultSub": "SIZA, watergebruiklisensie, FBO",
    "comp.docs": "11 dokumente",
    "comp.ggap": "GlobalG.A.P.-sertifikaat",
    "comp.expires": "verval oor 84 dae",
    "compare.eyebrow": "Vergelyk",
    "compare.title": "Goeie dagboeke. Een ketting.",
    "compare.lead":
      "Farmable is ’n goeie spuitdagboek en Tend is ’n goeie gewasbeplanner. Geeneen ken jou PUC nie, geeneen volg die krat deur die hek nie, en geeneen bring die gewig terug nie.",
    "compare.thBoer": "Boer",
    "compare.thBoerSub": "Saltbird · Suid-Afrika",
    "compare.thFarmable": "Farmable",
    "compare.thFarmableSub": "Oslo, Noorweë",
    "compare.thTend": "Tend",
    "compare.thTendSub": "Kalifornië, VSA",
    "compare.r1": "Blokke is jou geregistreerde boorde",
    "compare.r1s": "PUC · boordverwysings · kultivarkodes · markte",
    "compare.r1b": "Register-gesinkroniseer",
    "compare.r1f": "Selfverklaarde lande",
    "compare.r1t": "Selfverklaarde beddings",
    "compare.r2": "Kratbewaring van pluk tot pakhuishek",
    "compare.r2b": "Genoemde krat, versending, hek-aanname",
    "compare.r2f": "Gedrukte oesetiket",
    "compare.r2t": "Lot-ID en QR",
    "compare.r3": "Pakhuisgewigte kom terug na die blok",
    "compare.r4": "FCM-valrondtes en sanitasie-register",
    "compare.r4s": "DALRRD-vormig, vasgemaak aan jou seisoen",
    "compare.r4f": "Generiese moniteringstaak",
    "compare.r5": "Veilige-oesdatum uit die PHI, per blok",
    "compare.r5f": "Etiketwaarskuwings (volle data slegs VK)",
    "compare.r6": "Sertifikaatverval en ouditgereedheidstoets",
    "compare.r6b": "Nakoming-Locker",
    "compare.r6f": "Nakoming-kontroleskerm",
    "compare.r6t": "Verslae",
    "compare.r7": "Suid-Afrikaanse produkregister",
    "compare.r7s": "Wet 36 L-nommers op elke spuit",
    "compare.r7f": "9 registers, geeneen SA",
    "compare.r8": "Spuitdagboek op die foon, sonder internet",
    "compare.r8t": "metgesel-app",
    "compare.r9": "GlobalG.A.P.-vormige registeruitvoer",
    "compare.r9t": "Gedeeltelik",
    "compare.r10": "Werk met jou pakhuis se stelsel",
    "compare.r10b": "Dieselfde rekord",
    "compare.r10f": "Ondernemingsportaal, apart",
    "compare.r11": "Prys vir ’n boer",
    "compare.r11s": "per plaas, per jaar",
    "compare.r11b": "Gratis · Pro R4 950",
    "compare.r11f": "Gratis (5 take) · Pro R6 499",
    "compare.r11t": "Gratis · Pro ≈ R4 300 (USD)",
    "compare.note":
      "Mededingerbesonderhede uit hul openbare webtuistes, hulpsentrums en appwinkel-inskrywings, Augustus 2026. Farmable en Tend is handelsmerke van hul eienaars. Items gemerk seisoen ’26 verskyn voor die 2026-steenvrugteseisoen.",
    "onramp.eyebrow": "Reeds op Farmable?",
    "onramp.title": "Bring dit in ’n middag oor.",
    "onramp.p":
      "Koppel jou Farmable-rekening en jou lande, produkte, spuitgeskiedenis, spanne en uurstate kom in as Boer-blokke en -rekords. Hou albei aan tot jy seker is.",
    "onramp.s1": "Plak jou Farmable API-sleutel",
    "onramp.s2": "Pas jou lande by jou geregistreerde boorde — Boer stel die pare voor",
    "onramp.s3": "Drie seisoene se geskiedenis land op die regte blokke",
    "ph.eyebrow": "Vir pakhuise en koöperasies",
    "ph.title": "Bring jou hele verskaffersbasis.",
    "ph.p":
      "Elke boer wat jou voorsien, kry Boer gratis. Hul spuite, veilige-oesdatums en kratte arriveer op jou rekord voor die trok. Een register vir honderd plase — en die opbrengste wat jy plaas, gaan reguit terug na hul blokke.",
    "ph.s1": "Jou verskaffersregister word hul plaaslys",
    "ph.s2": "Jou hek neem hul kratte oor; jou weegbrug sluit die lus",
    "ph.s3": "Jou verpakking en opbrengste plaas terug na die boer se blok",
    "pricing.eyebrow": "Pryse",
    "pricing.title": "Gratis vir boere. Rand, per plaas, niks per hektaar nie.",
    "pricing.lead": "’n Plaas is ’n plaas — een PUC of ses, tien werkers of tweehonderd. Die prys beweeg nie wanneer die boord groei nie.",
    "plan.grower": "Boer",
    "plan.forever": "vir altyd",
    "plan.growerWho":
      "Elke boer. Gratis vir altyd as jy ’n pakhuis op Boer voorsien — en steeds gratis as jy nie.",
    "plan.g1": "Onbeperkte blokke, gebruikers en notas",
    "plan.g2": "Spuit-, voeding-, besproeiings- en verkenningsrekords",
    "plan.g3": "FCM-valrondtes, sanitasie, veilige-oesbord",
    "plan.g4": "Oeskratte, versending, hek-aanname, gewigte terug",
    "plan.g5": "Registersinkronisering, sertifikaatverval, dokumentkluis",
    "plan.pro": "Pro",
    "plan.perFarm": "per plaas · per jaar",
    "plan.proWho": "Vir die plaas wat geoudit word. Ongeveer R413 per maand, BTW ingesluit.",
    "plan.p1": "Alles in Boer",
    "plan.p2": "Oudituitvoere: spuitregister, FCM-verslag, oesregister, inspeksiepak",
    "plan.p3": "Produkvoorraad met koste per blok",
    "plan.p4": "Inteken, uurstate en stukloon",
    "plan.p5": "Adviseurtoegang en blokekonomie op werklike opbrengste",
    "plan.p6": "API-toegang",
    "plan.mostFarms": "Meeste plase",
    "plan.pack": "Pakhuis & koöp",
    "plan.letsTalk": "Kom ons praat",
    "plan.packWho": "Jou verskaffersbasis op een register, jou hek en weegbrug wat die lus sluit.",
    "plan.pk1": "Elke voorsienende boer op Boer, gratis",
    "plan.pk2": "Verskaffersnakoming-aansig voor die trok arriveer",
    "plan.pk3": "Hek- en weegbrugintegrasie",
    "plan.pk4": "Opbrengste teruggeplaas na boere se blokke",
    "plan.pk5": "Aanboord ter plaatse, in Afrikaans of Engels",
    "pricing.note":
      "Pryse in Suid-Afrikaanse Rand, BTW ingesluit. Pro word per plaas per jaar gefaktureer — nie per hektaar nie, nie per gebruiker nie. Kanselleer enige tyd; jou data gaan saam met jou.",
    "faq.eyebrow": "Vrae wat boere vra",
    "faq.title": "Reguit antwoorde.",
    "faq.q1": "Werk dit sonder sein in die boord?",
    "faq.a1":
      "Oesvullings wag reeds sonder internet en sinkroniseer by die skuur. Elke ander vaslegging — spuite, valrondtes, verkenning — werk sonder internet voor die 2026-steenvrugteseisoen.",
    "faq.q2": "Moet my pakhuis op Boer wees?",
    "faq.a2":
      "Nee. Boer is ’n volledige plaasrekord op sy eie. Wanneer jou pakhuis aansluit, sluit die hek en weegbrug die lus outomaties — niks verander aan jou kant nie.",
    "faq.q3": "Wie besit my data?",
    "faq.a3":
      "Jy. Dit word in Suid-Afrika gestoor, onder POPIA hanteer, en jy kan enige tyd alles uitvoer. Jou pakhuis sien net wat jy met hulle deel.",
    "faq.q4": "Ek is op Farmable. Verloor ek my geskiedenis?",
    "faq.a4":
      "Nee. Koppel jou Farmable-rekening en drie seisoene se lande, spuite, spanne en uurstate kom oor na jou geregistreerde boorde. Hou albei aan tot jy seker is.",
    "faq.q5": "Watter gewasse?",
    "faq.a5":
      "Sitrus, steenvrugte, kernvrugte, tafeldruiwe, avokado’s en makadamias eerste — die uitvoergewasse met FCM-, PHI- en GlobalG.A.P.-druk. Ander gewasse werk; die statutêre registers is hiervoor ingestel.",
    "faq.q6": "Watter tale is beskikbaar?",
    "faq.a6":
      "Engels, Afrikaans, Spaans, Italiaans en Japannees. Gebruik die taalkieser bo-aan hierdie bladsy. Die aangemelde app volg dieselfde keuse — taal word een keer op hierdie gasheer gestel.",
    "final.title": "Begin hierdie week met een blok.",
    "final.p":
      "Voeg jou PUC by, log Maandag se spuit, vul ’n krat by die eerste pluk. Teen die einde van die seisoen is die register geskryf en het die pakhuis dit al gesien.",
    "foot.product": "Produk",
    "foot.compare": "Vergelyk",
    "foot.pricing": "Pryse",
    "foot.packhouses": "Pakhuise",
    "foot.whatsNew": "Wat’s nuut",
    "foot.privacy": "Privaatheid",
    "foot.terms": "Voorwaardes",
    "foot.contact": "Kontak",
    "foot.saltbird": "Boer is ’n Saltbird-maatskappy · Gemaak in die Wes-Kaap",
    "foot.legal":
      "© 2026 Saltbird. Boer is onafhanklik en nie verbonde aan AgriOps, DALRRD, GlobalG.A.P., SIZA of PPECB nie. Fotografie: Freshness First, gebruik met toestemming. Franschhoek-beeldmateriaal © Paul Knipe.",
  },
  es: {
      "doc.title": "Boer de Saltbird",
      "nav.homeAria": "Inicio de Boer",
      "nav.chain": "Cómo se conecta",
      "nav.product": "Producto",
      "nav.compare": "Comparar",
      "nav.packhouses": "Para plantas de empaque",
      "nav.pricing": "Precios",
      "cta.signIn": "Entrar",
      "cta.startFree": "Empieza gratis",
      "cta.startPro": "Empieza Pro",
      "cta.seeHow": "Mira cómo se conecta ↓",
      "cta.talkPackhouse": "Hablemos de tu planta de empaque",
      "cta.talkToUs": "Háblanos",
      "lang.aria": "Idioma",
      "hero.eyebrow": "Para fruticultores sudafricanos",
      "hero.title": "La app de finca que pasa por la puerta de empaque.",
      "hero.titleHtml": "La app de finca que pasa <span class=\"thread\">por la puerta de empaque.</span>",
      "hero.lead": "Diario de pulverización, bins de cosecha y cumplimiento de exportación en un solo registro — del huerto a la báscula y de vuelta al bloque de origen. Hecha en el Cabo Occidental, en Rand, gratis para productores.",
      "hero.note": "Sin tarjeta. Sin límite de hectáreas. Funciona en el teléfono que tu capataz ya lleva.",
      "hero.credit": "Franschhoek, Cabo Occidental — imágenes © Paul Knipe",
      "stats.eyebrow": "Del bloque al conocimiento de embarque",
      "stats.title": "Un registro. Seis pases. Nada se escribe dos veces.",
      "stats.lead": "Un campo en una app-diario es lo que alguien tecleó. Un bloque en Boer es el huerto que conoce el registro, el bin que escaneó la puerta, y los kilos que vio la báscula.",
      "stats.taps": "toques para registrar una pulverización al final de la hilera",
      "stats.handoffs": "pases en el mismo registro",
      "stats.retype": "volver a teclear en la puerta de la planta",
      "stats.free": "para productores, para siempre",
      "record.aria": "Ejemplo de registro de bin",
      "record.weighed": "Pesado",
      "record.sprayed": "Pulverizado",
      "record.sprayedSub": "Cyanamid · 14 días PHI",
      "record.sprayedVal": "12 jun · seguro desde 26 jun",
      "record.picked": "Cosechado",
      "record.pickedSub": "Team Willem · 6 recolectores",
      "record.sent": "Enviado",
      "record.sentSub": "Nota de despacho DN-2287 · 24 bins",
      "record.adopted": "Adoptado en la puerta",
      "record.adoptedSub": "Lectura RFID · sin reescribir",
      "record.weighedWhat": "Pesado",
      "record.weighedSub": "Báscula de la planta",
      "record.return": "Retorno",
      "record.returnSub": "Vuelve a este bloque",
      "record.afterPackout": "después del empaque",
      "record.blockWeek": "Bloque ZA-OB-1142-07 esta semana",
      "trust.label": "Hecho para los registros que los productores sudafricanos ya contestan.",
      "loop.eyebrow": "Por qué cambian los productores",
      "loop.title": "Todo lo demás es un diario. Boer es toda la cadena.",
      "loop.lead": "Una app-diario se queda en una etiqueta impresa. Boer nombra el bin en la cosecha, lo pasa por la puerta y trae el peso de la planta de vuelta al bloque de origen.",
      "loop.blockCap": "El huerto de verdad",
      "loop.blockSpan": "PUC y refs de huerto registrados, códigos de cultivar, mercados aprobados — no un dibujo de alguien.",
      "loop.pickCap": "Registrado al final de la hilera",
      "loop.pickSpan": "Pulverización liberada por PHI, bin nombrado, equipo nombrado, hora sellada.",
      "loop.gateCap": "El bin pasa",
      "loop.gateSpan": "Nota de despacho, adopción RFID en la puerta, pesado y a la línea — sin reescribir.",
      "loop.returnCap": "De vuelta al bloque",
      "loop.returnSpan": "Empacado, paletizado, embarcado — toneladas y Rand vuelven al huerto y al equipo que lo cosechó.",
      "loop.oneRecord": "UN REGISTRO",
      "record.binId": "Bin 0412",
      "record.ref": "ZA-OB-1142-07 · Nadorcott · temporada 2026",
      "record.footTotal": "9.84 t · 24 bins",
      "trust.puc": "PUC · huerto",
      "trust.fcm": "registro FCM",
      "trust.lnum": "números L",
      "loop.blockN": "Bloque",
      "loop.pickN": "Cosecha",
      "loop.gateN": "Puerta",
      "loop.returnN": "Retorno",
      "nav.menuAria": "Menú",
      "loop.farmGateFarm": "Finca → puerta → finca",
      "chain.block": "Bloque",
      "chain.blockP": "Tu huerto registrado, sincronizado del registro, con cultivares y mercados en la ficha.",
      "chain.spray": "Pulverización",
      "chain.sprayP": "Producto, dosis, agua, operador, clima. El PHI pone solo la fecha de cosecha segura.",
      "chain.pick": "Cosecha",
      "chain.pickP": "Cada llenado nombra un bin — bloque, cultivar, recolector, hora.",
      "chain.gate": "Puerta",
      "chain.gateP": "La puerta de la planta lee el bin y adopta el llenado. Sin papel, sin bins perdidos.",
      "chain.pack": "Empaque",
      "chain.packP": "Pesado, clasificado, empacado en el registro que tú empezaste.",
      "chain.return": "Retorno",
      "chain.returnP": "Toneladas y Rand vuelven al bloque. Economía sobre retornos reales.",
      "product.eyebrow": "El producto",
      "product.title": "Hecha para quien está al final de la hilera, no para quien está en el escritorio.",
      "product.lead": "El capataz registra la pulverización a las seis de la mañana, no la oficina a las cinco de la tarde. Boer está hecha para eso — y la oficina recibe un registro que se escribe solo.",
      "capture.eyebrow": "Captura",
      "capture.title": "Tres toques desde el inicio. Legible al sol.",
      "capture.p": "Inicio, Fincas, Cosecha y un + grande. Pantallas claras, blancos grandes, el bloque elegido desde donde estás. Funciona sin señal en el huerto y sincroniza en el galpón.",
      "capture.offline": "sin conexión · season '26",
      "capture.li1": "Pulverización con L-number del producto, dosis, agua y mezcla de tanque",
      "capture.li2": "Fertilizar, fertigar, regar, explorar, ronda de trampas, sanidad",
      "capture.li3": "Llenado de cosecha con el bin nombrado, el equipo nombrado, la hora sellada",
      "phone.aria": "Teléfono mostrando una pulverización en captura",
      "phone.sprayJob": "Labor de pulverización",
      "phone.kProduct": "Producto",
      "phone.kRate": "Dosis · agua",
      "phone.kEquip": "Equipo · operador",
      "phone.kWeather": "Clima a las 06:40",
      "phone.kSafe": "Cosecha segura desde",
      "phone.navHome": "Inicio",
      "phone.navFarms": "Fincas",
      "phone.navHarvest": "Cosecha",
      "phone.navMore": "Más",
      "registry.eyebrow": "Verdad del registro",
      "registry.title": "Tus bloques son los bloques de verdad.",
      "registry.p": "Boer sincroniza tus unidades de producción y huertos desde el registro — PUC, refs de huerto, códigos de cultivar, registros de mercado y vencimiento de certificados. Cámbialo una vez; el inspector y la planta ven lo mismo.",
      "registry.li1": "Una fila de bloque por huerto registrado, de por vida",
      "registry.li2": "Mercados aprobados y vencimiento GGN en la ficha del bloque",
      "registry.li3": "Importa tu KML o dibújalo en el mapa — la ref no cambia",
      "registry.synced": "Sincronizado del registro",
      "registry.cardSub": "ZA-PU-1142 · 4 huertos · el GGN vence en 84 días",
      "registry.thOrchard": "Huerto",
      "registry.thCultivar": "Cultivar",
      "registry.thMarkets": "Mercados",
      "registry.thSafe": "Cosecha segura",
      "registry.phiNotClear": "PHI no liberado",
      "registry.clear": "liberado",
      "bins.eyebrow": "El circuito del bin",
      "bins.title": "Sabe qué salió de la finca y qué recibió la planta. Al kilogramo.",
      "bins.p": "Vacío, lleno, en almacén, enviado, adoptado en la puerta, pesado. La custodia viaja con el bin; el peso vuelve a ti. Cuando el rendimiento de empaque difiere de lo que enviaste, sabes qué bin, qué bloque, qué mañana.",
      "bins.li1": "Notas de despacho armadas con los bins que realmente llenaste",
      "bins.li2": "RFID o QR — la puerta adopta el llenado sin reescribir",
      "bins.li3": "Escala de tonelaje por bloque, por cultivar, por equipo",
      "bins.thBin": "Bin",
      "bins.thBlock": "Bloque",
      "bins.thTeam": "Equipo",
      "bins.thGate": "Puerta",
      "bins.thSent": "Enviado kg",
      "bins.thWeighed": "Pesado kg",
      "bins.more": "… 20 bins más",
      "bins.adopted": "24 / 24 adoptados",
      "comp.eyebrow": "Cumplimiento",
      "comp.title": "El registro se escribe solo.",
      "comp.p": "Rondas de trampas, sanidad del huerto, fechas de cosecha segura y vencimiento de certificados viven en el mismo registro que tus pulverizaciones. Cuando llama el auditor, el Casillero muestra qué está listo y qué no — y exporta el registro en la forma que piden.",
      "comp.exports": "exportaciones · season '26",
      "comp.li1": "Rondas de trampas FCM y sanidad, ancladas a tus fechas de temporada",
      "comp.li2": "Tablero de cosecha segura: qué bloques se pueden cosechar hoy",
      "comp.li3": "Registro de pulverización, registro de cosecha y paquete de inspección con forma GlobalG.A.P.",
      "comp.auditIn": "Klipfontein · auditoría en 19 días",
      "comp.toFix": "2 por corregir",
      "comp.readyFor": "listo para el inspector",
      "comp.sprayReg": "Registro de pulverización",
      "comp.sprayRegSub": "212 aplicaciones · cada una con L-number",
      "comp.complete": "completo",
      "comp.fcm": "Rondas de trampas FCM",
      "comp.fcmSub": "Semanal desde 1 oct · 14 trampas",
      "comp.san": "Sanidad del huerto",
      "comp.sanSub": "Registro de fruta caída",
      "comp.lastDays": "último: hace 2 días",
      "comp.safeBoard": "Tablero de cosecha segura",
      "comp.safeBoardSub": "Bloque 03 · PHI libera el 9 jul",
      "comp.oneBlock": "1 bloque no liberado",
      "comp.vault": "Bóveda de documentos",
      "comp.vaultSub": "SIZA, licencia de agua, FBO",
      "comp.docs": "11 documentos",
      "comp.ggap": "Certificado GlobalG.A.P.",
      "comp.expires": "vence en 84 días",
      "compare.eyebrow": "Comparar",
      "compare.title": "Buenos diarios. Una cadena.",
      "compare.lead": "Farmable es un buen diario de pulverización y Tend es un buen planificador de cultivos. Ninguno conoce tu PUC, ninguno sigue el bin por la puerta, y ninguno trae el peso de vuelta.",
      "compare.thBoer": "Boer",
      "compare.thBoerSub": "Saltbird · Sudáfrica",
      "compare.thFarmable": "Farmable",
      "compare.thFarmableSub": "Oslo, Noruega",
      "compare.thTend": "Tend",
      "compare.thTendSub": "California, EE. UU.",
      "compare.r1": "Los bloques son tus huertos registrados",
      "compare.r1s": "PUC · refs de huerto · códigos de cultivar · mercados",
      "compare.r1b": "Sincronizado del registro",
      "compare.r1f": "Campos autodeclarados",
      "compare.r1t": "Camas autodeclaradas",
      "compare.r2": "Custodia del bin de la cosecha a la puerta de la planta",
      "compare.r2b": "Bin nombrado, despacho, adopción en la puerta",
      "compare.r2f": "Etiqueta de cosecha impresa",
      "compare.r2t": "Lot ID y QR",
      "compare.r3": "Los pesos de la planta vuelven al bloque",
      "compare.r4": "Rondas de trampas FCM y registro de sanidad",
      "compare.r4s": "Con forma DALRRD, anclado a tu temporada",
      "compare.r4f": "Tarea de monitoreo genérica",
      "compare.r5": "Fecha de cosecha segura desde el PHI, por bloque",
      "compare.r5f": "Alertas de etiqueta (datos completos solo UK)",
      "compare.r6": "Vencimiento de certificados y chequeo de auditoría",
      "compare.r6b": "Casillero de cumplimiento",
      "compare.r6f": "Panel de cumplimiento",
      "compare.r6t": "Informes",
      "compare.r7": "Registro de productos sudafricano",
      "compare.r7s": "L-numbers de Act 36 en cada pulverización",
      "compare.r7f": "9 registros, ninguno SA",
      "compare.r8": "Diario de pulverización en el teléfono, sin conexión",
      "compare.r8t": "app compañera",
      "compare.r9": "Exportación de registro con forma GlobalG.A.P.",
      "compare.r9t": "Parcial",
      "compare.r10": "Funciona con el sistema de tu planta",
      "compare.r10b": "El mismo registro",
      "compare.r10f": "Portal empresarial, aparte",
      "compare.r11": "Precio para un productor",
      "compare.r11s": "por finca, por año",
      "compare.r11b": "Gratis · Pro R4 950",
      "compare.r11f": "Gratis (5 labores) · Pro R6 499",
      "compare.r11t": "Gratis · Pro ≈ R4 300 (USD)",
      "compare.note": "Datos de competidores de sus sitios públicos, centros de ayuda y fichas de tienda de aplicaciones, agosto 2026. Farmable y Tend son marcas de sus dueños. Los elementos marcados season '26 salen antes de la temporada de carozo 2026.",
      "onramp.eyebrow": "¿Ya estás en Farmable?",
      "onramp.title": "Tráelo en una tarde.",
      "onramp.p": "Conecta tu cuenta Farmable y tus campos, productos, historial de pulverización, equipos y hojas de tiempo entran como bloques y registros Boer. Mantén ambos hasta que estés seguro.",
      "onramp.s1": "Pega tu clave API de Farmable",
      "onramp.s2": "Empareja tus campos con tus huertos registrados — Boer sugiere los pares",
      "onramp.s3": "Tres temporadas de historial aterrizan en los bloques correctos",
      "ph.eyebrow": "Para plantas de empaque y cooperativas",
      "ph.title": "Trae toda tu base de proveedores.",
      "ph.p": "Cada productor que te abastece recibe Boer gratis. Sus pulverizaciones, fechas de cosecha segura y bins llegan a tu registro antes que el camión. Un registro para cien fincas — y los retornos que publicas van directo a sus bloques.",
      "ph.s1": "Tu registro de proveedores se vuelve su lista de fincas",
      "ph.s2": "Tu puerta adopta sus bins; tu báscula cierra el circuito",
      "ph.s3": "Tu rendimiento de empaque y retornos vuelven al bloque del productor",
      "pricing.eyebrow": "Precios",
      "pricing.title": "Gratis para productores. Rand, por finca, nada por hectárea.",
      "pricing.lead": "Una finca es una finca — un PUC o seis, diez trabajadores o doscientos. El precio no se mueve cuando crece el huerto.",
      "plan.grower": "Productor",
      "plan.forever": "para siempre",
      "plan.growerWho": "Todo productor. Gratis para siempre si abasteces una planta en Boer — y sigue gratis si no.",
      "plan.g1": "Bloques, usuarios y notas ilimitados",
      "plan.g2": "Registros de pulverización, nutrición, riego y exploración",
      "plan.g3": "Rondas de trampas FCM, sanidad, tablero de cosecha segura",
      "plan.g4": "Bins de cosecha, despacho, adopción en la puerta, pesos de vuelta",
      "plan.g5": "Sincronización de registro, vencimiento de certificados, bóveda de documentos",
      "plan.pro": "Pro",
      "plan.perFarm": "por finca · por año",
      "plan.proWho": "Para la finca que se audita. Unos R413 al mes, VAT incluido.",
      "plan.p1": "Todo lo de Productor",
      "plan.p2": "Exportaciones de auditoría: registro de pulverización, informe FCM, registro de cosecha, paquete de inspección",
      "plan.p3": "Inventario de producto con costo por bloque",
      "plan.p4": "Fichaje, hojas de tiempo y destajo",
      "plan.p5": "Acceso de asesor y economía de bloque sobre retornos reales",
      "plan.p6": "Acceso API",
      "plan.mostFarms": "La mayoría",
      "plan.pack": "Planta y coop",
      "plan.letsTalk": "Hablemos",
      "plan.packWho": "Tu base de proveedores en un registro, tu puerta y báscula cerrando el circuito.",
      "plan.pk1": "Cada productor que abastece, en Productor, gratis",
      "plan.pk2": "Vista de cumplimiento de proveedores antes de que llegue el camión",
      "plan.pk3": "Integración de puerta y báscula",
      "plan.pk4": "Retornos publicados en los bloques de los productores",
      "plan.pk5": "Puesta en marcha en sitio, en Afrikaans o English",
      "pricing.note": "Precios en Rand sudafricano, VAT incluido. Pro se factura por finca por año — no por hectárea, no por usuario. Cancela cuando quieras; tus datos se van contigo.",
      "faq.eyebrow": "Preguntas que hacen los productores",
      "faq.title": "Respuestas directas.",
      "faq.q1": "¿Funciona sin señal en el huerto?",
      "faq.a1": "Los llenados de cosecha ya se encolan sin conexión y sincronizan en el galpón. El resto de la captura — pulverizaciones, rondas de trampas, exploración — va sin conexión antes de la temporada de carozo 2026.",
      "faq.q2": "¿Necesito que mi planta esté en Boer?",
      "faq.a2": "No. Boer es un registro de finca completo por sí solo. Cuando tu planta se suma, la puerta y la báscula cierran el circuito solas — de tu lado no cambia nada.",
      "faq.q3": "¿Quién es dueño de mis datos?",
      "faq.a3": "Tú. Se guardan en Sudáfrica, se tratan bajo POPIA, y puedes exportar todo en cualquier momento. Tu planta solo ve lo que compartes con ella.",
      "faq.q4": "Estoy en Farmable. ¿Pierdo mi historial?",
      "faq.a4": "No. Conecta tu cuenta Farmable y tres temporadas de campos, pulverizaciones, equipos y hojas de tiempo pasan a tus huertos registrados. Mantén ambos hasta que estés seguro.",
      "faq.q5": "¿Qué cultivos?",
      "faq.a5": "Cítricos, carozo, pomáceas, uva de mesa, aguacate y macadamias primero — los cultivos de exportación con presión de FCM, PHI y GlobalG.A.P. Otros cultivos funcionan; los registros estatutarios están afinados para estos.",
      "faq.q6": "¿Qué idiomas hay?",
      "faq.a6": "English, Afrikaans, Español, Italiano y japonés. Usa el control de idioma arriba de esta página. La app con sesión sigue la misma elección — el idioma se fija una vez en este host.",
      "final.title": "Empieza esta semana con un bloque.",
      "final.p": "Añade tu PUC, registra la pulverización del lunes, llena un bin en la primera cosecha. Al final de la temporada el registro está escrito y la planta ya lo vio.",
      "foot.product": "Producto",
      "foot.compare": "Comparar",
      "foot.pricing": "Precios",
      "foot.packhouses": "Plantas de empaque",
      "foot.whatsNew": "Novedades",
      "foot.privacy": "Privacidad",
      "foot.terms": "Términos",
      "foot.contact": "Contacto",
      "foot.saltbird": "Boer es una empresa Saltbird · Hecha en el Cabo Occidental",
      "foot.legal": "© 2026 Saltbird. Boer es independiente y no está afiliada a AgriOps, DALRRD, GlobalG.A.P., SIZA ni PPECB. Fotografía: Freshness First, usada con permiso. Imágenes de Franschhoek © Paul Knipe.",
    },
  it: {
      "doc.title": "Boer di Saltbird",
      "nav.homeAria": "Home Boer",
      "nav.chain": "Come si collega",
      "nav.product": "Prodotto",
      "nav.compare": "Confronta",
      "nav.packhouses": "Per i magazzini",
      "nav.pricing": "Prezzi",
      "cta.signIn": "Accedi",
      "cta.startFree": "Inizia gratis",
      "cta.startPro": "Inizia Pro",
      "cta.seeHow": "Guarda come si collega ↓",
      "cta.talkPackhouse": "Parliamone del tuo magazzino",
      "cta.talkToUs": "Parliamoci",
      "lang.aria": "Lingua",
      "hero.eyebrow": "Per i frutticoltori sudafricani",
      "hero.title": "L'app da campo che passa dal cancello del magazzino.",
      "hero.titleHtml": "L'app da campo che passa <span class=\"thread\">dal cancello del magazzino.</span>",
      "hero.lead": "Diario trattamenti, cassoni di raccolta e conformità all'export su un solo registro — dal frutteto alla pesa e di nuovo al blocco da cui è partito. Fatta nel Western Cape, in Rand, gratis per i produttori.",
      "hero.note": "Nessuna carta. Nessun tetto di ettari. Funziona sul telefono che il tuo caposquadra ha già in tasca.",
      "hero.credit": "Franschhoek, Western Cape — riprese © Paul Knipe",
      "stats.eyebrow": "Dal blocco alla polizza di carico",
      "stats.title": "Un registro. Sei passaggi. Niente digitato due volte.",
      "stats.lead": "Un campo in un'app-diario è quello che qualcuno ha scritto. Un blocco in Boer è il frutteto che il registro conosce, il cassone che il cancello ha scansionato, e i chili che la pesa ha visto.",
      "stats.taps": "tocchi per registrare un trattamento in fondo al filare",
      "stats.handoffs": "passaggi sullo stesso registro",
      "stats.retype": "ridigitare al cancello del magazzino",
      "stats.free": "per i produttori, per sempre",
      "record.aria": "Esempio di registro cassone",
      "record.weighed": "Pesato",
      "record.sprayed": "Trattato",
      "record.sprayedSub": "Cyanamid · 14 giorni PHI",
      "record.sprayedVal": "12 giu · sicuro dal 26 giu",
      "record.picked": "Raccolto",
      "record.pickedSub": "Team Willem · 6 raccoglitori",
      "record.sent": "Inviato",
      "record.sentSub": "Bolla di spedizione DN-2287 · 24 cassoni",
      "record.adopted": "Adottato al cancello",
      "record.adoptedSub": "Lettura RFID · niente ridigitare",
      "record.weighedWhat": "Pesato",
      "record.weighedSub": "Pesa del magazzino",
      "record.return": "Ritorno",
      "record.returnSub": "Torna su questo blocco",
      "record.afterPackout": "dopo il confezionamento",
      "record.blockWeek": "Blocco ZA-OB-1142-07 questa settimana",
      "trust.label": "Pensato per i registri a cui i produttori sudafricani già rispondono.",
      "loop.eyebrow": "Perché i produttori cambiano",
      "loop.title": "Tutto il resto è un diario. Boer è tutta la filiera.",
      "loop.lead": "Un'app-diario si ferma a un'etichetta stampata. Boer nomina il cassone alla raccolta, lo fa passare dal cancello e riporta il peso del magazzino al blocco da cui è partito.",
      "loop.blockCap": "Il frutteto vero",
      "loop.blockSpan": "PUC e rif. frutteto registrati, codici cultivar, mercati approvati — non una forma disegnata da qualcuno.",
      "loop.pickCap": "Registrato in fondo al filare",
      "loop.pickSpan": "Trattamento sbloccato dal PHI, cassone nominato, squadra nominata, ora timbrata.",
      "loop.gateCap": "Il cassone passa",
      "loop.gateSpan": "Bolla di spedizione, adozione RFID al cancello, pesato e in linea — niente ridigitare.",
      "loop.returnCap": "Di nuovo al blocco",
      "loop.returnSpan": "Confezionato, palletizzato, spedito — tonnellate e Rand tornano al frutteto e alla squadra che l'ha raccolto.",
      "loop.oneRecord": "UN REGISTRO",
      "record.binId": "Cassone 0412",
      "record.ref": "ZA-OB-1142-07 · Nadorcott · stagione 2026",
      "record.footTotal": "9.84 t · 24 cassoni",
      "trust.puc": "PUC · frutteto",
      "trust.fcm": "registro FCM",
      "trust.lnum": "numeri L",
      "loop.blockN": "Blocco",
      "loop.pickN": "Raccolta",
      "loop.gateN": "Cancello",
      "loop.returnN": "Ritorno",
      "nav.menuAria": "Menu",
      "loop.farmGateFarm": "Azienda → cancello → azienda",
      "chain.block": "Blocco",
      "chain.blockP": "Il tuo frutteto registrato, sincronizzato dal registro, con cultivar e mercati sulla scheda.",
      "chain.spray": "Trattamento",
      "chain.sprayP": "Prodotto, dose, acqua, operatore, meteo. Il PHI imposta da solo la data di raccolta sicura.",
      "chain.pick": "Raccolta",
      "chain.pickP": "Ogni riempimento nomina un cassone — blocco, cultivar, raccoglitore, ora.",
      "chain.gate": "Cancello",
      "chain.gateP": "Il cancello del magazzino legge il cassone e adotta il riempimento. Niente carta, niente cassoni persi.",
      "chain.pack": "Confezionamento",
      "chain.packP": "Pesato, calibrato, confezionato sul registro che hai iniziato tu.",
      "chain.return": "Ritorno",
      "chain.returnP": "Tonnellate e Rand tornano al blocco. Economia su ritorni veri.",
      "product.eyebrow": "Il prodotto",
      "product.title": "Fatta per chi sta in fondo al filare, non per chi sta alla scrivania.",
      "product.lead": "Il caposquadra registra il trattamento alle sei del mattino, non l'ufficio alle cinque del pomeriggio. Boer è fatta per quello — e l'ufficio ottiene un registro che si scrive da solo.",
      "capture.eyebrow": "Registra",
      "capture.title": "Tre tocchi dalla schermata iniziale. Leggibile al sole.",
      "capture.p": "Inizio, Aziende, Raccolta e un + grosso. Schermi chiari, bersagli grandi, il blocco scelto da dove sei. Funziona senza rete in frutteto e sincronizza al capannone.",
      "capture.offline": "senza rete · season '26",
      "capture.li1": "Trattamento con L-number del prodotto, dose, acqua e miscela in botte",
      "capture.li2": "Concimare, fertirrigare, irrigare, monitorare, giro trappole, sanificazione",
      "capture.li3": "Riempimento raccolta con cassone nominato, squadra nominata, ora timbrata",
      "phone.aria": "Telefono che mostra un trattamento in registrazione",
      "phone.sprayJob": "Lavoro di trattamento",
      "phone.kProduct": "Prodotto",
      "phone.kRate": "Dose · acqua",
      "phone.kEquip": "Attrezzatura · operatore",
      "phone.kWeather": "Meteo alle 06:40",
      "phone.kSafe": "Raccolta sicura da",
      "phone.navHome": "Inizio",
      "phone.navFarms": "Aziende",
      "phone.navHarvest": "Raccolta",
      "phone.navMore": "Altro",
      "registry.eyebrow": "Verità del registro",
      "registry.title": "I tuoi blocchi sono i blocchi veri.",
      "registry.p": "Boer sincronizza le tue unità di produzione e i frutteti dal registro — PUC, rif. frutteto, codici cultivar, registrazioni di mercato e scadenza dei certificati. Cambialo una volta; l'ispettore e il magazzino vedono la stessa cosa.",
      "registry.li1": "Una riga di blocco per ogni frutteto registrato, per sempre",
      "registry.li2": "Mercati approvati e scadenza GGN sulla scheda del blocco",
      "registry.li3": "Importa il tuo KML o disegnalo sulla mappa — il rif. resta lo stesso",
      "registry.synced": "Sincronizzato dal registro",
      "registry.cardSub": "ZA-PU-1142 · 4 frutteti · il GGN scade tra 84 giorni",
      "registry.thOrchard": "Frutteto",
      "registry.thCultivar": "Cultivar",
      "registry.thMarkets": "Mercati",
      "registry.thSafe": "Raccolta sicura",
      "registry.phiNotClear": "PHI non sbloccato",
      "registry.clear": "sbloccato",
      "bins.eyebrow": "Il circuito del cassone",
      "bins.title": "Sai cosa è uscito dall'azienda e cosa ha ricevuto il magazzino. Al chilogrammo.",
      "bins.p": "Vuoto, pieno, in deposito, inviato, adottato al cancello, pesato. La custodia viaggia col cassone; il peso torna a te. Quando il confezionamento differisce da quello che hai inviato, sai quale cassone, quale blocco, quale mattina.",
      "bins.li1": "Bolle di spedizione fatte coi cassoni che hai davvero riempito",
      "bins.li2": "RFID o QR — il cancello adotta il riempimento senza ridigitare",
      "bins.li3": "Scala di tonnellaggio per blocco, per cultivar, per squadra",
      "bins.thBin": "Cassone",
      "bins.thBlock": "Blocco",
      "bins.thTeam": "Squadra",
      "bins.thGate": "Cancello",
      "bins.thSent": "Inviato kg",
      "bins.thWeighed": "Pesato kg",
      "bins.more": "… altri 20 cassoni",
      "bins.adopted": "24 / 24 adottati",
      "comp.eyebrow": "Conformità",
      "comp.title": "Il registro si scrive da solo.",
      "comp.p": "Giri trappole, sanificazione del frutteto, date di raccolta sicura e scadenza dei certificati vivono sullo stesso registro dei tuoi trattamenti. Quando chiama l'auditor, l'Archivio mostra cosa è pronto e cosa no — ed esporta il registro nella forma che chiedono.",
      "comp.exports": "esportazioni · season '26",
      "comp.li1": "Giri trappole FCM e sanificazione, ancorati alle date della tua stagione",
      "comp.li2": "Bacheca raccolta sicura: quali blocchi si possono raccogliere oggi",
      "comp.li3": "Registro trattamenti, registro raccolta e pacchetto ispezione in forma GlobalG.A.P.",
      "comp.auditIn": "Klipfontein · audit tra 19 giorni",
      "comp.toFix": "2 da sistemare",
      "comp.readyFor": "pronto per l'ispettore",
      "comp.sprayReg": "Registro trattamenti",
      "comp.sprayRegSub": "212 applicazioni · ognuna con L-number",
      "comp.complete": "completo",
      "comp.fcm": "Giri trappole FCM",
      "comp.fcmSub": "Settimanale dal 1 ott · 14 trappole",
      "comp.san": "Sanificazione del frutteto",
      "comp.sanSub": "Registro della frutta caduta",
      "comp.lastDays": "ultimo: 2 giorni fa",
      "comp.safeBoard": "Bacheca raccolta sicura",
      "comp.safeBoardSub": "Blocco 03 · PHI si sblocca il 9 lug",
      "comp.oneBlock": "1 blocco non sbloccato",
      "comp.vault": "Cassaforte documenti",
      "comp.vaultSub": "SIZA, licenza d'uso dell'acqua, FBO",
      "comp.docs": "11 documenti",
      "comp.ggap": "Certificato GlobalG.A.P.",
      "comp.expires": "scade tra 84 giorni",
      "compare.eyebrow": "Confronta",
      "compare.title": "Buoni diari. Una filiera.",
      "compare.lead": "Farmable è un ottimo diario trattamenti e Tend è un ottimo pianificatore colturale. Nessuno conosce il tuo PUC, nessuno segue il cassone dal cancello, e nessuno riporta il peso.",
      "compare.thBoer": "Boer",
      "compare.thBoerSub": "Saltbird · Sudafrica",
      "compare.thFarmable": "Farmable",
      "compare.thFarmableSub": "Oslo, Norvegia",
      "compare.thTend": "Tend",
      "compare.thTendSub": "California, USA",
      "compare.r1": "I blocchi sono i tuoi frutteti registrati",
      "compare.r1s": "PUC · rif. frutteto · codici cultivar · mercati",
      "compare.r1b": "Sincronizzato dal registro",
      "compare.r1f": "Campi autodichiarati",
      "compare.r1t": "Aiuole autodichiarate",
      "compare.r2": "Custodia del cassone dalla raccolta al cancello del magazzino",
      "compare.r2b": "Cassone nominato, spedizione, adozione al cancello",
      "compare.r2f": "Etichetta di raccolta stampata",
      "compare.r2t": "Lot ID e QR",
      "compare.r3": "I pesi del magazzino tornano al blocco",
      "compare.r4": "Giri trappole FCM e registro sanificazione",
      "compare.r4s": "In forma DALRRD, ancorato alla tua stagione",
      "compare.r4f": "Lavoro di monitoraggio generico",
      "compare.r5": "Data di raccolta sicura dal PHI, per blocco",
      "compare.r5f": "Avvisi etichetta (dati completi solo UK)",
      "compare.r6": "Scadenza certificati e controllo di prontezza all'audit",
      "compare.r6b": "Archivio conformità",
      "compare.r6f": "Cruscotto conformità",
      "compare.r6t": "Resoconti",
      "compare.r7": "Registro prodotti sudafricano",
      "compare.r7s": "L-number Act 36 su ogni trattamento",
      "compare.r7f": "9 registri, nessuno SA",
      "compare.r8": "Diario trattamenti sul telefono, senza rete",
      "compare.r8t": "app complementare",
      "compare.r9": "Esportazione del registro in forma GlobalG.A.P.",
      "compare.r9t": "Parziale",
      "compare.r10": "Funziona col sistema del tuo magazzino",
      "compare.r10b": "Lo stesso registro",
      "compare.r10f": "Portale aziendale, a parte",
      "compare.r11": "Prezzo per un produttore",
      "compare.r11s": "per azienda, per anno",
      "compare.r11b": "Gratis · Pro R4 950",
      "compare.r11f": "Gratis (5 lavori) · Pro R6 499",
      "compare.r11t": "Gratis · Pro ≈ R4 300 (USD)",
      "compare.note": "Dettagli dei concorrenti dai loro siti pubblici, centri assistenza e schede dei negozi di app, agosto 2026. Farmable e Tend sono marchi dei rispettivi titolari. Gli elementi marcati season '26 escono prima della stagione 2026 della frutta a nocciolo.",
      "onramp.eyebrow": "Già su Farmable?",
      "onramp.title": "Portalo via in un pomeriggio.",
      "onramp.p": "Collega il tuo account Farmable e campi, prodotti, storico trattamenti, squadre e fogli ore arrivano come blocchi e registri Boer. Tieni entrambi finché non sei sicuro.",
      "onramp.s1": "Incolla la tua chiave API Farmable",
      "onramp.s2": "Abbina i tuoi campi ai frutteti registrati — Boer suggerisce le coppie",
      "onramp.s3": "Tre stagioni di storico atterrano sui blocchi giusti",
      "ph.eyebrow": "Per magazzini e cooperative",
      "ph.title": "Porta tutta la tua base fornitori.",
      "ph.p": "Ogni produttore che ti rifornisce riceve Boer gratis. I loro trattamenti, date di raccolta sicura e cassoni arrivano sul tuo registro prima del camion. Un registro per cento aziende — e i ritorni che pubblichi vanno dritti sui loro blocchi.",
      "ph.s1": "Il tuo registro fornitori diventa la loro lista aziende",
      "ph.s2": "Il tuo cancello adotta i loro cassoni; la tua pesa chiude il circuito",
      "ph.s3": "Il tuo confezionamento e i ritorni tornano sul blocco del produttore",
      "pricing.eyebrow": "Prezzi",
      "pricing.title": "Gratis per i produttori. Rand, per azienda, niente per ettaro.",
      "pricing.lead": "Un'azienda è un'azienda — un PUC o sei, dieci operai o duecento. Il prezzo non si muove quando il frutteto cresce.",
      "plan.grower": "Produttore",
      "plan.forever": "per sempre",
      "plan.growerWho": "Ogni produttore. Gratis per sempre se rifornisci un magazzino su Boer — e resta gratis anche se no.",
      "plan.g1": "Blocchi, utenti e note illimitati",
      "plan.g2": "Registri trattamenti, nutrizione, irrigazione e monitoraggio",
      "plan.g3": "Giri trappole FCM, sanificazione, bacheca raccolta sicura",
      "plan.g4": "Cassoni di raccolta, spedizione, adozione al cancello, pesi di ritorno",
      "plan.g5": "Sincronizzazione registro, scadenza certificati, cassaforte documenti",
      "plan.pro": "Pro",
      "plan.perFarm": "per azienda · per anno",
      "plan.proWho": "Per l'azienda che va in audit. Circa R413 al mese, VAT inclusa.",
      "plan.p1": "Tutto di Produttore",
      "plan.p2": "Esportazioni per l'audit: registro trattamenti, resoconto FCM, registro raccolta, pacchetto ispezione",
      "plan.p3": "Inventario prodotti con costo per blocco",
      "plan.p4": "Timbratura, fogli ore e cottimo",
      "plan.p5": "Accesso consulente ed economia di blocco su ritorni veri",
      "plan.p6": "Accesso API",
      "plan.mostFarms": "La maggioranza",
      "plan.pack": "Magazzino e coop",
      "plan.letsTalk": "Parliamone",
      "plan.packWho": "La tua base fornitori su un registro, cancello e pesa che chiudono il circuito.",
      "plan.pk1": "Ogni produttore che rifornisce, su Produttore, gratis",
      "plan.pk2": "Vista conformità fornitori prima che arrivi il camion",
      "plan.pk3": "Integrazione cancello e pesa",
      "plan.pk4": "Ritorni pubblicati sui blocchi dei produttori",
      "plan.pk5": "Avvio in sede, in Afrikaans o English",
      "pricing.note": "Prezzi in Rand sudafricani, VAT inclusa. Pro si fattura per azienda per anno — non per ettaro, non per utente. Disdici quando vuoi; i tuoi dati escono con te.",
      "faq.eyebrow": "Domande che fanno i produttori",
      "faq.title": "Risposte diritte.",
      "faq.q1": "Funziona senza segnale in frutteto?",
      "faq.a1": "I riempimenti di raccolta già vanno in coda senza rete e sincronizzano al capannone. Ogni altra registrazione — trattamenti, giri trappole, monitoraggio — va senza rete prima della stagione 2026 della frutta a nocciolo.",
      "faq.q2": "Devo avere il magazzino su Boer?",
      "faq.a2": "No. Boer è un registro aziendale completo da solo. Quando il tuo magazzino entra, cancello e pesa chiudono il circuito da soli — da te non cambia niente.",
      "faq.q3": "Di chi sono i miei dati?",
      "faq.a3": "Tuoi. Stanno in Sudafrica, gestiti sotto POPIA, e puoi esportare tutto in qualsiasi momento. Il magazzino vede solo ciò che condividi.",
      "faq.q4": "Sono su Farmable. Perdo lo storico?",
      "faq.a4": "No. Collega il tuo account Farmable e tre stagioni di campi, trattamenti, squadre e fogli ore passano sui tuoi frutteti registrati. Tieni entrambi finché non sei sicuro.",
      "faq.q5": "Quali colture?",
      "faq.a5": "Agrumi, frutta a nocciolo, pomacee, uva da tavola, avocado e macadamia per primi — le colture da export con pressione FCM, PHI e GlobalG.A.P. Le altre colture funzionano; i registri statutari sono tarati su queste.",
      "faq.q6": "Quali lingue ci sono?",
      "faq.a6": "English, Afrikaans, Español, Italiano e giapponese. Usa il controllo lingua in alto in questa pagina. L'app autenticata segue la stessa scelta — la lingua si imposta una volta su questo host.",
      "final.title": "Parti questa settimana con un blocco.",
      "final.p": "Aggiungi il tuo PUC, registra il trattamento di lunedì, riempi un cassone al primo raccolto. A fine stagione il registro è scritto e il magazzino l'ha già visto.",
      "foot.product": "Prodotto",
      "foot.compare": "Confronta",
      "foot.pricing": "Prezzi",
      "foot.packhouses": "Magazzini",
      "foot.whatsNew": "Novità",
      "foot.privacy": "Privacy",
      "foot.terms": "Termini",
      "foot.contact": "Contatti",
      "foot.saltbird": "Boer è una società Saltbird · Fatta nel Western Cape",
      "foot.legal": "© 2026 Saltbird. Boer è indipendente e non affiliata ad AgriOps, DALRRD, GlobalG.A.P., SIZA o PPECB. Fotografia: Freshness First, usata con permesso. Riprese Franschhoek © Paul Knipe.",
    },
  ja: {
      "doc.title": "SaltbirdのBoer",
      "nav.homeAria": "Boerホーム",
      "nav.chain": "つながる仕組み",
      "nav.product": "製品",
      "nav.compare": "比較",
      "nav.packhouses": "選果場向け",
      "nav.pricing": "料金",
      "cta.signIn": "ログイン",
      "cta.startFree": "無料ではじめる",
      "cta.startPro": "Proをはじめる",
      "cta.seeHow": "つながる仕組みを見る ↓",
      "cta.talkPackhouse": "選果場について相談する",
      "cta.talkToUs": "相談する",
      "lang.aria": "言語",
      "hero.eyebrow": "南アフリカの果実生産者へ",
      "hero.title": "選果場の門を通り抜ける農場アプリ。",
      "hero.titleHtml": "<span class=\"thread\">選果場の門を通り抜ける</span>農場アプリ。",
      "hero.lead": "散布日記、収穫ビン、輸出コンプライアンスを一つの記録に — 果樹園から計量台へ、そして元の区画へ戻ります。西ケープで作り、Rand建て、生産者は無料です。",
      "hero.note": "カード不要。ヘクタール上限なし。現場責任者がすでに持っているスマートフォンで動きます。",
      "hero.credit": "Franschhoek、西ケープ — 映像 © Paul Knipe",
      "stats.eyebrow": "区画から船荷証券まで",
      "stats.title": "一つの記録。六つの受け渡し。二度打ちはなし。",
      "stats.lead": "日記アプリの欄は、誰かが打った文字です。Boerの区画は、登録簿が知る果樹園、門が読み取ったビン、計量台が見たキログラムです。",
      "stats.taps": "列の端で散布を記録するタップ数",
      "stats.handoffs": "同じ記録上の受け渡し",
      "stats.retype": "選果場の門での再入力",
      "stats.free": "生産者は、ずっと無料",
      "record.aria": "ビン記録の例",
      "record.weighed": "計量済み",
      "record.sprayed": "散布済み",
      "record.sprayedSub": "Cyanamid · PHI 14日",
      "record.sprayedVal": "6月12日 · 6月26日から安全",
      "record.picked": "収穫済み",
      "record.pickedSub": "Team Willem · 6名",
      "record.sent": "出荷済み",
      "record.sentSub": "出荷伝票 DN-2287 · 24ビン",
      "record.adopted": "門で取り込み",
      "record.adoptedSub": "RFID読取 · 再入力なし",
      "record.weighedWhat": "計量済み",
      "record.weighedSub": "選果場の計量台",
      "record.return": "戻り",
      "record.returnSub": "この区画に戻る",
      "record.afterPackout": "パックアウト後",
      "record.blockWeek": "区画 ZA-OB-1142-07 今週",
      "trust.label": "南アフリカの生産者がすでに答えている登録簿のかたちに合わせてあります。",
      "loop.eyebrow": "切り替える理由",
      "loop.title": "ほかは日記。Boerはチェーン全体。",
      "loop.lead": "日記アプリは印刷ラベルで止まります。Boerは収穫時にビンに名前を付け、門を通し、選果場の重量を元の区画へ戻します。",
      "loop.blockCap": "本物の果樹園",
      "loop.blockSpan": "登録済みのPUCと果樹園参照、品種コード、承認市場 — 誰かが描いた図形ではありません。",
      "loop.pickCap": "列の端で記録",
      "loop.pickSpan": "PHIで散布解除、ビンに名前、チームに名前、時刻を打刻。",
      "loop.gateCap": "ビンが通る",
      "loop.gateSpan": "出荷伝票、門でのRFID取り込み、計量してラインへ — 再入力なし。",
      "loop.returnCap": "区画へ戻る",
      "loop.returnSpan": "梱包、パレット化、出荷 — トンとRandが果樹園と収穫したチームに戻ります。",
      "loop.oneRecord": "一つの記録",
      "record.binId": "ビン 0412",
      "record.ref": "ZA-OB-1142-07 · Nadorcott · 2026シーズン",
      "record.footTotal": "9.84 t · 24ビン",
      "trust.puc": "PUC · 果樹園",
      "trust.fcm": "FCM登録簿",
      "trust.lnum": "L番号",
      "loop.blockN": "区画",
      "loop.pickN": "収穫",
      "loop.gateN": "門",
      "loop.returnN": "戻り",
      "nav.menuAria": "メニュー",
      "loop.farmGateFarm": "農場 → 門 → 農場",
      "chain.block": "区画",
      "chain.blockP": "登録済みの果樹園。登録簿から同期し、カードに品種と市場が載っています。",
      "chain.spray": "散布",
      "chain.sprayP": "製品、散布量、水量、作業者、天候。PHIが安全収穫日を自分で決めます。",
      "chain.pick": "収穫",
      "chain.pickP": "充填のたびにビンを指名 — 区画、品種、収穫者、時刻。",
      "chain.gate": "門",
      "chain.gateP": "選果場の門がビンを読み、充填を取り込みます。紙なし、行方不明のビンなし。",
      "chain.pack": "選果",
      "chain.packP": "計量、等級、梱包は、あなたが始めた記録の上で行われます。",
      "chain.return": "戻り",
      "chain.returnP": "トンとRandが区画に戻ります。実際のリターンで見る経済です。",
      "product.eyebrow": "製品",
      "product.title": "机の人ではなく、列の端にいる人のために。",
      "product.lead": "現場責任者は朝6時に散布を記録します。事務所の午後5時ではありません。Boerはそのために作ってあり — 事務所には、自分で書き上がる登録簿が届きます。",
      "capture.eyebrow": "記録",
      "capture.title": "ホームから3タップ。日差しの下でも読める。",
      "capture.p": "ホーム、農場、収穫、そして大きな+。明るい画面、大きなタップ面、立っている場所から区画を選びます。果樹園ではオフラインで動き、納屋で同期します。",
      "capture.offline": "オフライン · season '26",
      "capture.li1": "製品のL-number、散布量、水量、タンクミックスで散布",
      "capture.li2": "施肥、施肥灌水、灌漑、巡回確認、トラップ巡回、衛生管理",
      "capture.li3": "ビン・チームに名前を付け、時刻を打刻した収穫充填",
      "phone.aria": "散布作業を記録中のスマートフォン",
      "phone.sprayJob": "散布作業",
      "phone.kProduct": "製品",
      "phone.kRate": "散布量 · 水",
      "phone.kEquip": "機材 · 作業者",
      "phone.kWeather": "06:40の天候",
      "phone.kSafe": "安全収穫開始",
      "phone.navHome": "ホーム",
      "phone.navFarms": "農場",
      "phone.navHarvest": "収穫",
      "phone.navMore": "その他",
      "registry.eyebrow": "登録簿の事実",
      "registry.title": "あなたの区画は、本物の区画です。",
      "registry.p": "Boerは生産単位と果樹園を登録簿から同期します — PUC、果樹園参照、品種コード、市場登録、証明書の期限。一度変えれば、検査官も選果場も同じものを見ます。",
      "registry.li1": "登録果樹園ごとに一行の区画、ずっと",
      "registry.li2": "承認市場とGGN期限を区画カードに表示",
      "registry.li3": "KMLを取り込むか地図に描く — 参照は変わりません",
      "registry.synced": "登録簿同期済み",
      "registry.cardSub": "ZA-PU-1142 · 4 園地 · GGN 期限まで84日",
      "registry.thOrchard": "果樹園",
      "registry.thCultivar": "品種",
      "registry.thMarkets": "市場",
      "registry.thSafe": "安全収穫",
      "registry.phiNotClear": "PHI未解除",
      "registry.clear": "解除済み",
      "bins.eyebrow": "ビンの循環",
      "bins.title": "農場を出たものと、選果場が受け取ったもの。キログラムまで。",
      "bins.p": "空、満杯、保管中、出荷、門で取り込み、計量。保管責任はビンと一緒に動き、重量はあなたに戻ります。パックアウトが送ったものと違えば、どのビン、どの区画、どの朝かが分かります。",
      "bins.li1": "実際に充填したビンから作る出荷伝票",
      "bins.li2": "RFIDまたはQR — 門が再入力なしで充填を取り込む",
      "bins.li3": "区画・品種・チームごとのトン数ラダー",
      "bins.thBin": "ビン",
      "bins.thBlock": "区画",
      "bins.thTeam": "チーム",
      "bins.thGate": "門",
      "bins.thSent": "出荷 kg",
      "bins.thWeighed": "計量 kg",
      "bins.more": "… ほか20ビン",
      "bins.adopted": "24 / 24 取り込み済み",
      "comp.eyebrow": "コンプライアンス",
      "comp.title": "登録簿は自分で書き上がります。",
      "comp.p": "トラップ巡回、果樹園の衛生管理、安全収穫日、証明書の期限は、散布と同じ記録に載ります。監査人が来たら、保管庫が準備済みと未完了を示し — 求められた形で登録簿を書き出します。",
      "comp.exports": "書き出し · season '26",
      "comp.li1": "FCMトラップ巡回と衛生管理。シーズン日程に固定",
      "comp.li2": "安全収穫ボード：今日収穫してよい区画",
      "comp.li3": "GlobalG.A.P.形の散布登録簿、収穫登録簿、検査パック",
      "comp.auditIn": "Klipfontein · 監査まで19日",
      "comp.toFix": "要修正 2件",
      "comp.readyFor": "検査官の準備完了",
      "comp.sprayReg": "散布登録簿",
      "comp.sprayRegSub": "212件 · すべてにL-number",
      "comp.complete": "完了",
      "comp.fcm": "FCMトラップ巡回",
      "comp.fcmSub": "10月1日から毎週 · 14トラップ",
      "comp.san": "果樹園の衛生管理",
      "comp.sanSub": "落下果実登録簿",
      "comp.lastDays": "最終：2日前",
      "comp.safeBoard": "安全収穫ボード",
      "comp.safeBoardSub": "区画 03 · PHI解除 7月9日",
      "comp.oneBlock": "未解除 1区画",
      "comp.vault": "書類保管庫",
      "comp.vaultSub": "SIZA、水利許可、FBO",
      "comp.docs": "11件の書類",
      "comp.ggap": "GlobalG.A.P.証明書",
      "comp.expires": "期限まで84日",
      "compare.eyebrow": "比較",
      "compare.title": "優れた日記。一本のチェーン。",
      "compare.lead": "Farmableは優れた散布日記、Tendは優れた作付プランナーです。どちらもPUCを知らず、門までビンを追わず、重量を戻しません。",
      "compare.thBoer": "Boer",
      "compare.thBoerSub": "Saltbird · 南アフリカ",
      "compare.thFarmable": "Farmable",
      "compare.thFarmableSub": "Oslo、ノルウェー",
      "compare.thTend": "Tend",
      "compare.thTendSub": "カリフォルニア、USA",
      "compare.r1": "区画は登録済みの果樹園",
      "compare.r1s": "PUC · 果樹園参照 · 品種コード · 市場",
      "compare.r1b": "登録簿同期",
      "compare.r1f": "自己申告の圃場",
      "compare.r1t": "自己申告のベッド",
      "compare.r2": "収穫から選果場の門までのビン保管責任",
      "compare.r2b": "指名ビン、出荷、門での取り込み",
      "compare.r2f": "印刷した収穫ラベル",
      "compare.r2t": "Lot IDとQR",
      "compare.r3": "選果場の重量が区画に戻る",
      "compare.r4": "FCMトラップ巡回と衛生管理登録簿",
      "compare.r4s": "DALRRD形、シーズンに固定",
      "compare.r4f": "汎用モニタリング作業",
      "compare.r5": "PHIから出す安全収穫日、区画ごと",
      "compare.r5f": "ラベル警告（完全データはUKのみ）",
      "compare.r6": "証明書期限と監査準備チェック",
      "compare.r6b": "コンプライアンス保管庫",
      "compare.r6f": "コンプライアンスダッシュボード",
      "compare.r6t": "レポート",
      "compare.r7": "南アフリカの製品登録簿",
      "compare.r7s": "すべての散布にAct 36のL-number",
      "compare.r7f": "9つの登録簿、SAはゼロ",
      "compare.r8": "スマートフォンの散布日記、オフライン",
      "compare.r8t": "補助アプリ",
      "compare.r9": "GlobalG.A.P.形の登録簿書き出し",
      "compare.r9t": "一部",
      "compare.r10": "選果場のシステムと連携",
      "compare.r10b": "同じ記録",
      "compare.r10f": "別の企業ポータル",
      "compare.r11": "生産者向け価格",
      "compare.r11s": "農場あたり、年額",
      "compare.r11b": "無料 · Pro R4 950",
      "compare.r11f": "無料（5件） · Pro R6 499",
      "compare.r11t": "無料 · Pro ≈ R4 300 (USD)",
      "compare.note": "競合の詳細は公開サイト、ヘルプセンター、アプリストア掲載（2026年8月）に基づきます。FarmableとTendは各権利者の商標です。season '26の項目は2026年核果シーズン前に出荷します。",
      "onramp.eyebrow": "すでにFarmableをお使いですか？",
      "onramp.title": "午後ひとつで移せます。",
      "onramp.p": "Farmableアカウントをつなげると、圃場、製品、散布履歴、チーム、勤務表がBoerの区画と記録として入ります。確信が持てるまで両方を動かせます。",
      "onramp.s1": "FarmableのAPIキーを貼り付け",
      "onramp.s2": "圃場を登録果樹園に対応づけ — Boerがペアを提案します",
      "onramp.s3": "3シーズン分の履歴が正しい区画に載ります",
      "ph.eyebrow": "選果場と協同組合へ",
      "ph.title": "サプライヤー基盤をまるごと。",
      "ph.p": "納入する生産者は全員、Boerが無料です。散布、安全収穫日、ビンがトラックより先にあなたの記録に届きます。百農場を一つの登録簿に — あなたが載せるリターンは、そのまま彼らの区画へ戻ります。",
      "ph.s1": "サプライヤー登録簿が、彼らの農場リストになります",
      "ph.s2": "門がビンを取り込み、計量台が循環を閉じます",
      "ph.s3": "パックアウトとリターンが生産者の区画に戻ります",
      "pricing.eyebrow": "料金",
      "pricing.title": "生産者は無料。Rand建て、農場単位、ヘクタール課金なし。",
      "pricing.lead": "農場は農場です — PUCが一つでも六つでも、作業者が十人でも二百人でも。果樹園が広がっても価格は動きません。",
      "plan.grower": "生産者",
      "plan.forever": "ずっと",
      "plan.growerWho": "すべての生産者。Boer上の選果場に納入していればずっと無料 — 納入していなくても無料のままです。",
      "plan.g1": "区画・ユーザー・メモは無制限",
      "plan.g2": "散布、栄養、灌漑、巡回確認の記録",
      "plan.g3": "FCMトラップ巡回、衛生管理、安全収穫ボード",
      "plan.g4": "収穫ビン、出荷、門での取り込み、重量の戻り",
      "plan.g5": "登録簿同期、証明書期限、書類保管庫",
      "plan.pro": "Pro",
      "plan.perFarm": "農場あたり · 年額",
      "plan.proWho": "監査を受ける農場向け。月あたり約R413、VAT込み。",
      "plan.p1": "生産者プランのすべて",
      "plan.p2": "監査書き出し：散布登録簿、FCM報告、収穫登録簿、検査パック",
      "plan.p3": "区画ごとのコストつき製品在庫",
      "plan.p4": "出勤打刻、勤務表、出来高",
      "plan.p5": "アドバイザー権限と、実リターンの区画経済",
      "plan.p6": "APIアクセス",
      "plan.mostFarms": "ほとんどの農場",
      "plan.pack": "選果場・協同組合",
      "plan.letsTalk": "相談する",
      "plan.packWho": "サプライヤー基盤を一つの登録簿に。門と計量台が循環を閉じます。",
      "plan.pk1": "納入する全生産者が生産者プラン、無料",
      "plan.pk2": "トラック到着前のサプライヤーコンプライアンス表示",
      "plan.pk3": "門と計量台の連携",
      "plan.pk4": "リターンを生産者の区画へ投稿",
      "plan.pk5": "現地オンボーディング、AfrikaansまたはEnglish",
      "pricing.note": "価格は南アフリカRand、VAT込みです。Proは農場あたり年額 — ヘクタールでもユーザー数でもありません。いつでも解約でき、データは一緒に書き出せます。",
      "faq.eyebrow": "生産者が聞くこと",
      "faq.title": "まっすぐな答え。",
      "faq.q1": "果樹園で電波がなくても動きますか？",
      "faq.a1": "収穫充填はすでにオフラインでためておき、納屋で同期します。散布、トラップ巡回、巡回確認など、ほかの記録は2026年核果シーズン前にオフライン対応します。",
      "faq.q2": "選果場がBoerに入っている必要はありますか？",
      "faq.a2": "いいえ。Boer単体で完結した農場記録です。選果場が加わると、門と計量台が自動で循環を閉じます — あなたの側は何も変わりません。",
      "faq.q3": "データの所有者は誰ですか？",
      "faq.a3": "あなたです。南アフリカに保管し、POPIAの下で扱い、いつでもすべて書き出せます。選果場が見るのは、あなたが共有したものだけです。",
      "faq.q4": "Farmableを使っています。履歴は消えますか？",
      "faq.a4": "消えません。Farmableアカウントをつなげると、3シーズン分の圃場、散布、チーム、勤務表が登録果樹園に載ります。確信が持てるまで両方を動かせます。",
      "faq.q5": "どの作物ですか？",
      "faq.a5": "まずは柑橘、核果、仁果、テーブルグレープ、アボカド、マカダミア — FCM、PHI、GlobalG.A.P.の圧力がある輸出作物です。ほかの作物も使えます。法定登録簿はこれら向けに合わせています。",
      "faq.q6": "どの言語が使えますか？",
      "faq.a6": "English、Afrikaans、Español、Italiano、日本語です。このページ上部の言語コントロールを使ってください。ログイン後のアプリも同じ選択に従います — 言語はこのホストで一度だけ設定されます。",
      "final.title": "今週、一つの区画から。",
      "final.p": "PUCを追加し、月曜の散布を記録し、最初の収穫でビンを充填してください。シーズンの終わりには登録簿が書き上がり、選果場はすでにそれを見ています。",
      "foot.product": "製品",
      "foot.compare": "比較",
      "foot.pricing": "料金",
      "foot.packhouses": "選果場",
      "foot.whatsNew": "新着",
      "foot.privacy": "プライバシー",
      "foot.terms": "利用規約",
      "foot.contact": "お問い合わせ",
      "foot.saltbird": "BoerはSaltbirdの会社です · 西ケープ製",
      "foot.legal": "© 2026 Saltbird。Boerは独立しており、AgriOps、DALRRD、GlobalG.A.P.、SIZA、PPECBとは提携していません。写真：Freshness First（許諾済み）。Franschhoek映像 © Paul Knipe。",
  },
};

export function boerLandingLocale(code: string | null | undefined): BoerLandingLocale {
  return (BOER_LANDING_LOCALES as readonly string[]).includes(code ?? "")
    ? (code as BoerLandingLocale)
    : "en";
}
